import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface SlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  width?: string;
  children: React.ReactNode;
  className?: string;
}

const SlidePanel: React.FC<SlidePanelProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  width = 'max-w-[520px]',
  children,
  className = '',
}) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 z-modal flex justify-end pointer-events-none"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="absolute inset-0 bg-surface-overlay backdrop-blur-sm animate-fade-in pointer-events-auto"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`relative h-full ${width} w-full bg-gradient-to-b from-[var(--surface-panel-from)] to-[var(--surface-panel-to)] border-l border-subtle shadow-2xl overflow-y-auto animate-slide-in-right pointer-events-auto flex flex-col ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'slide-panel-title' : undefined}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-subtle flex items-center justify-between shrink-0">
          <div className="flex-1 min-w-0">
            {title && (
              <h2
                id="slide-panel-title"
                className="text-lg font-semibold text-content"
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-xs text-content-tertiary mt-0.5">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-content-muted hover:text-content-secondary hover:bg-surface-primary cursor-pointer transition-colors border-none outline-none shrink-0 ml-3"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default SlidePanel;
